
import React, { useState } from 'react';

interface UsernameRequiredScreenProps {
  telegramUser: any;
  onRetry: () => void;
}

const translations = {
  en: {
    title: "Telegram Username Required",
    subtitle: "Create a @username to continue",
    step1Title: "Open Settings",
    step1Desc: "Go to Telegram Settings",
    step2Title: "Set Username",
    step2Desc: 'Go to "Set Username" section',
    step3Title: "Choose Username",
    step3Desc: "Pick a unique @username",
    step4Title: "Come Back",
    step4Desc: "Return here to complete registration"
  },
  it: {
    title: "Username Telegram Richiesto",
    subtitle: "Crea un @username per continuare",
    step1Title: "Apri Impostazioni",
    step1Desc: "Vai nelle Impostazioni di Telegram",
    step2Title: "Imposta Username",
    step2Desc: 'Vai nella sezione "Imposta Username"',
    step3Title: "Scegli Username",
    step3Desc: "Scegli un @username unico",
    step4Title: "Torna Qui",
    step4Desc: "Ritorna qui per completare la registrazione"
  },
  es: {
    title: "Nombre de Usuario de Telegram Requerido",
    subtitle: "Crea un @username para continuar",
    step1Title: "Abrir Ajustes",
    step1Desc: "Ve a Ajustes de Telegram",
    step2Title: "Establecer Nombre de Usuario",
    step2Desc: 'Ve a la sección "Establecer Nombre de Usuario"',
    step3Title: "Elegir Nombre de Usuario",
    step3Desc: "Elige un @username único",
    step4Title: "Volver Aquí",
    step4Desc: "Regresa aquí para completar el registro"
  },
  de: {
    title: "Telegram-Benutzername Erforderlich",
    subtitle: "Erstelle einen @username um fortzufahren",
    step1Title: "Einstellungen Öffnen",
    step1Desc: "Gehe zu Telegram-Einstellungen",
    step2Title: "Benutzername Festlegen",
    step2Desc: 'Gehe zum Abschnitt "Benutzername festlegen"',
    step3Title: "Benutzername Wählen",
    step3Desc: "Wähle einen einzigartigen @username",
    step4Title: "Zurückkommen",
    step4Desc: "Kehre hierher zurück, um die Registrierung abzuschließen"
  },
  pt: {
    title: "Nome de Usuário do Telegram Necessário",
    subtitle: "Crie um @username para continuar",
    step1Title: "Abrir Configurações",
    step1Desc: "Vá para Configurações do Telegram",
    step2Title: "Definir Nome de Usuário",
    step2Desc: 'Vá para a seção "Definir Nome de Usuário"',
    step3Title: "Escolher Nome de Usuário",
    step3Desc: "Escolha um @username único",
    step4Title: "Voltar Aqui",
    step4Desc: "Retorne aqui para completar o registro"
  },
  fr: {
    title: "Nom d'Utilisateur Telegram Requis",
    subtitle: "Créez un @username pour continuer",
    step1Title: "Ouvrir les Paramètres",
    step1Desc: "Allez dans les Paramètres de Telegram",
    step2Title: "Définir le Nom d'Utilisateur",
    step2Desc: 'Allez dans la section "Définir le nom d\'utilisateur"',
    step3Title: "Choisir un Nom d'Utilisateur",
    step3Desc: "Choisissez un @username unique",
    step4Title: "Revenir Ici",
    step4Desc: "Revenez ici pour terminer l'inscription"
  }
};

const languageFlags = {
  en: "🇬🇧",
  it: "🇮🇹",
  es: "🇪🇸",
  de: "🇩🇪",
  pt: "🇧🇷",
  fr: "🇫🇷"
};

export function UsernameRequiredScreen({ telegramUser, onRetry }: UsernameRequiredScreenProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<keyof typeof translations>('en');
  const t = translations[selectedLanguage];

  const handleOpenTelegramSettings = () => {
    // Open Telegram settings to create username
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink('https://t.me/settings');
    } else {
      window.open('https://t.me/settings', '_blank');
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center p-6 tg-bg">
      <div className="w-full max-w-md space-y-6">
        {/* Utya GIF animata */}
        <div className="flex justify-center mb-8">
          <img
            src="/assets/images/utya (1).gif"
            alt="Utya duck"
            className="w-32 h-32"
          />
        </div>

        {/* Titolo */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-white">
            {t.title}
          </h1>
          <p className="text-gray-400 text-sm">
            {t.subtitle}
          </p>
        </div>

        {/* Language Selector con bandiere */}
        <div className="flex justify-center gap-1.5">
          {(Object.keys(languageFlags) as Array<keyof typeof languageFlags>).map((lang) => (
            <button
              key={lang}
              onClick={() => setSelectedLanguage(lang)}
              className={`text-xl p-2 rounded-lg transition-all ${
                selectedLanguage === lang
                  ? 'bg-blue-500/30 scale-105 ring-2 ring-blue-400'
                  : 'bg-white/5 hover:bg-white/10'
              }`}
              title={lang.toUpperCase()}
            >
              {languageFlags[lang]}
            </button>
          ))}
        </div>

        {/* Step cards moderni */}
        <div className="space-y-3">
          {/* Step 1 */}
          <div 
            className="p-4 rounded-2xl tg-secondary-bg border border-white/5 flex items-start gap-4 cursor-pointer hover:border-white/10 transition-all active:scale-95"
            onClick={handleOpenTelegramSettings}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 font-bold text-lg">1</span>
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-white font-semibold mb-1">{t.step1Title}</h3>
              <p className="text-gray-400 text-sm">{t.step1Desc}</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="p-4 rounded-2xl tg-secondary-bg border border-white/5 flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 font-bold text-lg">2</span>
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-white font-semibold mb-1">{t.step2Title}</h3>
              <p className="text-gray-400 text-sm">{t.step2Desc}</p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="p-4 rounded-2xl tg-secondary-bg border border-white/5 flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 font-bold text-lg">3</span>
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-white font-semibold mb-1">{t.step3Title}</h3>
              <p className="text-gray-400 text-sm">{t.step3Desc}</p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="p-4 rounded-2xl tg-secondary-bg border border-white/5 flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="text-green-400 font-bold text-lg">✓</span>
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-white font-semibold mb-1">{t.step4Title}</h3>
              <p className="text-gray-400 text-sm">{t.step4Desc}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsernameRequiredScreen;
